import React, {DependencyList, EffectCallback, useCallback, useEffect, useRef, useState} from "react";
import UserInteractionDataStorage, {DataStorageType} from "@/providers/UserInteractionDataStorage";

type WindowSize = {
    windowWidth: number;
    windowHeight: number;
    isMobile: boolean;
};

export const useResize = (timeoutInMs: number = 500): WindowSize => {
    const [windowSize, setWindowSize] = useState<WindowSize>({windowWidth: window.innerWidth, windowHeight: window.innerHeight, isMobile: window.innerWidth < 768});

    useEffect(() => {
        let timeout: NodeJS.Timeout;
        const handleResize = () => {
            clearTimeout(timeout);

            timeout = setTimeout(() => {
                setWindowSize({windowWidth: window.innerWidth, windowHeight: window.innerHeight, isMobile: window.innerWidth < 768});
            }, timeoutInMs);
        };
        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, [timeoutInMs]);

    return windowSize;
};

export const useClickOutsideEffect = (refs: React.RefObject<HTMLElement>[], active: boolean, callback: () => void): void => {
    const handleClickOutside = useCallback((event: MouseEvent) => {
        if (refs.every(ref => ref.current && !ref.current.contains(event.target as Node))) {
            callback();
        }
    }, [refs, callback]);

    useEffect(() => {
        if (active) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleClickOutside, active]);
};

export const useLocalStorage = <T>(key: string, defaultValue: T, type: DataStorageType = DataStorageType.Page): [T, React.Dispatch<React.SetStateAction<T>>] => {
    const [value, setValue] = useState<T>(() => UserInteractionDataStorage.get(key, defaultValue, type));

    useEffect(() => {
        UserInteractionDataStorage.set(key, value, type);
    }, [key, value, type]);

    return [value, setValue];
};

export const useDidUpdateEffect = (effect: EffectCallback, deps?: DependencyList): void => {
    const isMounted = useRef(false);

    useEffect(() => {
        if (isMounted.current) {
            return effect();
        } else {
            isMounted.current = true;
        }
    }, deps);
};
